import { Button } from "@progress/kendo-react-buttons";
import { AlertCircle, ArrowRight, ChevronsRight } from "lucide-react";
import React from "react";

export interface SlidePanelValidationSummaryItem {
  id: string;
  label: React.ReactNode;
  ariaLabel?: string;
  onClick: () => void;
}

export interface SlidePanelValidationSummaryProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  items: SlidePanelValidationSummaryItem[];
  goToFirstLabel: React.ReactNode;
  onGoToFirst: () => void;
  dataTestId?: string;
  goToFirstDataTestId?: string;
  itemDataTestId?: string;
}

const SlidePanelValidationSummary: React.FC<
  SlidePanelValidationSummaryProps
> = ({
  title,
  description,
  items,
  goToFirstLabel,
  onGoToFirst,
  dataTestId = "slide-panel-validation-summary",
  goToFirstDataTestId = "slide-panel-validation-summary-go-first",
  itemDataTestId = "slide-panel-validation-summary-item",
}) => {
  if (!items.length) return null;

  return (
    <div
      className="tlx-panel-validation-summary"
      data-test-id={dataTestId}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="tlx-panel-validation-summary__top">
        <div className="tlx-panel-validation-summary__icon" aria-hidden="true">
          <AlertCircle size={18} />
        </div>
        <div className="tlx-panel-validation-summary__copy">
          <strong className="tlx-panel-validation-summary__title">
            {title}
          </strong>
          {description && (
            <div className="tlx-panel-validation-summary__description">
              {description}
            </div>
          )}
        </div>
        <Button
          type="button"
          size="small"
          className="tlx-panel-validation-summary__primary-action"
          data-test-id={goToFirstDataTestId}
          onClick={onGoToFirst}
        >
          {goToFirstLabel}
          <ArrowRight size={14} aria-hidden="true" />
        </Button>
      </div>

      <div className="tlx-panel-validation-summary__divider" />

      <div className="tlx-panel-validation-summary__items">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className="tlx-panel-validation-summary__item"
            data-test-id={itemDataTestId}
            onClick={item.onClick}
            aria-label={item.ariaLabel}
          >
            <span className="tlx-panel-validation-summary__item-index">
              {index + 1}
            </span>
            <span className="tlx-panel-validation-summary__item-label">
              {item.label}
            </span>
            <ChevronsRight size={16} aria-hidden="true" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default SlidePanelValidationSummary;
